import { useCallback, useEffect, useRef, useState } from "react";
import type { FallbackTier, NormalizedFeed } from "@sikhio/shared";

interface FeedState<T> {
  data: T[];
  ageMinutes: number;
  fallbackTier: FallbackTier | "loading";
  loadedAt: string | null;
  error: string | null;
  /** Upstream `meta.note` — explains *why* a feed is degraded (e.g. "Missing
   *  AQICN_TOKEN"). Surfaced in the UI so a dead toggle is never silent. */
  note?: string;
}

const STORAGE_PREFIX = "sikhio:feed:";
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000;
const UNAVAILABLE_AFTER_FAILS = 3;
const REQUEST_TIMEOUT_MS = 10_000;

function storageKey(path: string): string {
  return `${STORAGE_PREFIX}${path}`;
}

function readLocal<T>(path: string): FeedState<T> | null {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(storageKey(path)) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state: FeedState<T>; savedAt: number };
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > MAX_CACHE_AGE_MS) return null;
    return { ...parsed.state, fallbackTier: "cache" };
  } catch {
    return null;
  }
}

function writeLocal<T>(path: string, state: FeedState<T>): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(storageKey(path), JSON.stringify({ state, savedAt: Date.now() }));
    // axiom-audit-ignore-next-line back -- localStorage quota or Safari private-mode; cached state is non-essential (live fetch still works). Operator impact: zero.
  } catch (err) {
    if (typeof console !== "undefined") console.warn("[useFeed] cache write failed", path, err);
  }
}

const emptyInitial = <T,>(): FeedState<T> => ({
  data: [],
  ageMinutes: 0,
  fallbackTier: "loading",
  loadedAt: null,
  error: null,
});

async function fetchOnce(url: string, signal: AbortSignal): Promise<Response> {
  const ctrl = new AbortController();
  let timedOut = false;
  const timeoutId = window.setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, REQUEST_TIMEOUT_MS);
  const abort = () => ctrl.abort();
  signal.addEventListener("abort", abort, { once: true });
  try {
    return await fetch(url, { signal: ctrl.signal, cache: "no-store" });
  } catch (err) {
    if (timedOut) throw new Error(`Request timed out after ${REQUEST_TIMEOUT_MS / 1000}s`);
    throw err;
  } finally {
    window.clearTimeout(timeoutId);
    signal.removeEventListener("abort", abort);
  }
}

async function fetchWithRetry(url: string, signal: AbortSignal, retries = 2): Promise<Response> {
  let lastErr: unknown;
  for (let i = 0; i <= retries; i++) {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    try {
      const res = await fetchOnce(url, signal);
      if (res.ok) return res;
      if (res.status < 500 && res.status !== 429) throw new Error(`${res.status} ${res.statusText}`);
      lastErr = new Error(`${res.status} ${res.statusText}`);
    } catch (err) {
      lastErr = err;
      if (err instanceof DOMException && err.name === "AbortError") throw err;
    }
    if (i < retries && !signal.aborted) {
      await new Promise((r) => setTimeout(r, 800 * (i + 1)));
    }
  }
  throw lastErr ?? new Error("Fetch failed after retries");
}

export function useFeed<T>(path: string | null, pollMs: number): FeedState<T> & { refetch: () => void } {
  const [state, setState] = useState<FeedState<T>>(() =>
    path ? (readLocal<T>(path) ?? emptyInitial<T>()) : emptyInitial<T>()
  );
  const inflight = useRef<AbortController | null>(null);
  const runRef = useRef<() => Promise<void>>(async () => {});
  const failCount = useRef(0);

  useEffect(() => {
    // null path = caller wants this feed suspended (e.g. via useDeferredReady).
    // We return early with no polling, no error state, no fetch — caller can
    // render their "loading" placeholder and the panel stays quiet.
    if (!path) return;
    let cancelled = false;

    const run = async () => {
      inflight.current?.abort();
      const ctrl = new AbortController();
      inflight.current = ctrl;
      try {
        const sep = path.includes("?") ? "&" : "?";
        const url = `${path}${sep}_=${Date.now()}`;
        const res = await fetchWithRetry(url, ctrl.signal);
        const json = (await res.json()) as NormalizedFeed<T>;
        if (cancelled) return;
        failCount.current = 0;
        setState((prev) => {
          // Short-circuit when upstream hasn't moved — same fetchedAt means same payload.
          // Avoids cascading re-renders of dashboard chips and tickers on every poll.
          if (
            prev.loadedAt === json.meta.fetchedAt &&
            prev.fallbackTier === json.meta.fallbackTier &&
            prev.error === null
          ) {
            return prev;
          }
          const next: FeedState<T> = {
            data: json.features,
            ageMinutes: json.meta.ageMinutes,
            fallbackTier: json.meta.fallbackTier,
            loadedAt: json.meta.fetchedAt,
            error: null,
            note: json.meta.note,
          };
          writeLocal(path, next);
          return next;
        });
      } catch (err) {
        const isAbort = err instanceof DOMException && err.name === "AbortError"
          || err instanceof Error && err.name === "AbortError";
        if (cancelled || isAbort) return;
        failCount.current += 1;
        const errMsg = err instanceof Error ? err.message : String(err);
        const fails = failCount.current;
        setState((prev) => {
          const tier: FallbackTier | "loading" =
            fails >= UNAVAILABLE_AFTER_FAILS
              ? "unavailable"
              : prev.loadedAt
                ? prev.fallbackTier
                : "loading";
          if (prev.error === errMsg && prev.fallbackTier === tier) return prev;
          return { ...prev, error: errMsg, fallbackTier: tier };
        });
      }
    };
    runRef.current = run;

    run();
    const id = setInterval(run, pollMs);
    return () => {
      cancelled = true;
      clearInterval(id);
      inflight.current?.abort();
    };
  }, [path, pollMs]);

  const refetch = useCallback(() => runRef.current(), []);
  return { ...state, refetch };
}
